import { Popup } from "@progress/kendo-react-popup";
import React, { useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { storeAssigneeList } from "../../../../actions/task";
import { inviteUsers } from "../../../../components/Admin/admin.service";
import { userRolesOwners } from "../../../../components/Projects/project.constants";
import { icon, label, notifyIcon, number } from "../../../../config";
import PageFilterMultiselect from "../../../../shared/components/PagesFilterMultiselect/PageFilterMultiselect";
import { addProjectUsers } from "../../../../shared/services/projects.service";
import { validateEmail } from "../../../../shared/validators/validator";
import store from "../../../../store";
import { getNotification } from "../../../../utils/common";

const AddMembersPopup = ({ anchor, show, onClose, projectId }) => {
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const defaultDetails = useSelector((state) => state.tasks?.defaultDetails);
  const projectMembers = useSelector((state) => state.team?.projectMembers);
  const projectDetails = useSelector((state) => state.projectSidebar?.projectDetails);
  const { QueueId } = projectDetails || {};

  const wrapper = useRef(null);
  const [filterSelections, setFilterSelections] = useState({});
  const [selectedProjectUsers, setSelectedProjectUsers] = useState([]);
  const [filterText, setFilterText] = useState("");

  const userRole = useMemo(() => userRolesOwners?.find((d) => d?.value === number?.FIVE?.toString()), []);

  const allUserData = useMemo(() => {
    const list = defaultDetails?.assigneeList || [];
    let users = list.map((singleUser) => ({ value: singleUser?.value, label: singleUser?.label, email: singleUser?.Email }));
    // Filter out existing project members
    if (projectMembers?.length) {
      users = users?.filter((item) => !projectMembers?.some((member) => String(item?.value) === String(member?.UserId)));
    }
    // Filter out current user
    users = users?.filter((item) => item?.value !== user?.id);
    return users;
  }, [defaultDetails?.assigneeList, projectMembers, user?.id]);

  const handleFilterSelections = (newSelections) => {
    setFilterSelections(newSelections);
    const selectedValues = newSelections?.projectUsers ? newSelections?.projectUsers?.split(",") : [];
    const selectedUsers = allUserData?.filter((u) => selectedValues?.includes(u?.value?.toString()));
    setSelectedProjectUsers(selectedUsers);
  };

  const listNoDataRender = (element) => {
    const noData = (
      <div className='input-group-append p-2'>
        <button
          className='btn btn-primary btn-sm'
          id='button-team-invite-users'
          type='button'
          onClick={async () => {
            await handleInviteUsers(filterText);
          }}>
          {label?.INVITE_USER}
        </button>
      </div>
    );
    return React.cloneElement(element, { ...element.props }, noData);
  };

  const submitProjectUsers = async (userIds, addUserResponse, fromDropdown) => {
    const state = store.getState();
    let assigneeList = state?.tasks?.defaultDetails?.assigneeList || [];
    if (addUserResponse) {
      assigneeList = [
        ...assigneeList,
        { label: addUserResponse?.Name, value: addUserResponse?.userId, Email: addUserResponse?.Email, PhotoLink: null },
      ];
    }
    if (fromDropdown && Array.isArray(userIds)) {
      userIds = userIds?.map((d) => d?.value);
    }
    const payload = {
      roleId: userRole?.value,
      projectId: projectId,
      entityType: number?.THREE,
      createdBy: user?.id,
      queueId: QueueId,
      projectUsers: Array.isArray(userIds) ? userIds?.toString() : String(userIds),
      roleType: userRolesOwners?.find((item) => item?.value === userRole?.value)?.label,
      accountStatus: fromDropdown ? number?.ZERO : number?.TWO,
    };
    await dispatch(storeAssigneeList(assigneeList));
    await dispatch(addProjectUsers(payload));
    onClose && onClose();
  };

  const handleInviteUsers = async (email) => {
    const normalized = email?.trim();
    if (!normalized || !validateEmail(normalized)) {
      getNotification(label?.INVALID_EMAILS || "Invalid Email(s)", notifyIcon?.WARNING_ICON || "warning");
      return;
    }
    const userInfo = { id: user?.id, companyId: user?.companyId };
    const response = await inviteUsers([normalized], userInfo, "project");
    if (response) {
      await submitProjectUsers(response?.userId, response, false);
    }
  };

  const handleDone = async () => {
    if (!selectedProjectUsers?.length) return;
    await submitProjectUsers(selectedProjectUsers, null, true);
  };

  if (!show) return null;

  return (
    <Popup
      appendTo={anchor?.current}
      show={show}
      anchor={anchor?.current}
      animate={false}
      anchorAlign={{ horizontal: "left", vertical: "top" }}
      popupAlign={{ horizontal: "left", vertical: "top" }}
      popupClass={"dt-popup add-members-popup"}>
      <div ref={wrapper} style={{ width: 350 }}>
        <div className='dt-popup-header d-flex justify-content-between align-items-center pt-2 pb-2 pl-3 pr-3'>
          <div className='dt-popup-title'>{label?.INVITE_USER}</div>
        </div>
        <div className='dt-popup-body pb-3'>
          <div className='row'>
            <div className='col-md-9'>
              <PageFilterMultiselect
                multiSelectData={allUserData}
                placeholder={label?.SEARCH}
                selectedData={selectedProjectUsers}
                filterKey='projectUsers'
                filterSelections={filterSelections}
                setFilterSelections={handleFilterSelections}
                showPlaceHolder={false}
                tagsLength={number?.ONE}
                listNoDataRender={listNoDataRender}
                onFilterValueChange={(v) => setFilterText(v)}
                autoFocus
              />
            </div>
            <div className='col-md-3'>
              <div className='d-flex  mt-2'>
                <span className='mr-2' onClick={() => onClose && onClose()}>
                  {icon?.CANCEL}
                </span>
                <span className='mr-2' disabled={!selectedProjectUsers?.length} onClick={handleDone}>
                  {icon?.DONE}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Popup>
  );
};

export default AddMembersPopup;
